/**
 * ShadowMode.jsx — Phase 4: Live Shadow Mode (Quality Gating)
 *
 * Continuously learns from manual calls without any explicit upload action.
 * Agents configure which bot to train, minimum call length, outcome filters,
 * how often to re-derive persona, and quality gating (strictMode toggle).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Mic2, Bot, Clock, TrendingUp, RefreshCw, CheckSquare, Square,
  ChevronRight, Loader2, Save, Play, Info, Zap, BarChart3,
  Filter, ArrowDownToLine, Users, ShieldCheck, ShieldAlert, Eye,
  AlertTriangle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPut, apiPost } from '../lib/api.js';
import TopBar from '../components/TopBar.jsx';

// ─── Default outcome options (superset of known CIVIX dispositions) ──────────

const ALL_OUTCOMES = [
  { value: 'sold',           label: 'Sold / Application' },
  { value: 'callback',       label: 'Callback' },
  { value: 'follow_up',      label: 'Follow-Up' },
  { value: 'qualified',      label: 'Qualified' },
  { value: 'booked_appointment', label: 'Appointment Booked' },
  { value: 'not_interested', label: 'Not Interested' },
  { value: 'no_answer',      label: 'No Answer' },
  { value: 'dnc',            label: 'DNC' },
  { value: 'no_sale',        label: 'No Sale' },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmt(date) {
  if (!date) return '—';
  return new Date(date).toLocaleString();
}

function fmtDuration(sec) {
  if (!sec) return '—';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

// ─── Stat card ───────────────────────────────────────────────────────────────

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="panel p-4 flex items-center gap-4">
      <div className="w-10 h-10 rounded-lg bg-civix-purple/20 flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-civix-purple" />
      </div>
      <div>
        <div className="text-2xl font-bold text-white">{value}</div>
        <div className="text-xs text-white/50">{label}</div>
        {sub && <div className="text-[10px] text-white/30 mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

// ─── Recent call row ─────────────────────────────────────────────────────────

// Phase 4: decision color mapping
const DECISION_PILL = {
  pass:   { label: 'Pass',   className: 'bg-green-400/10 text-green-400'  },
  review: { label: 'Review', className: 'bg-yellow-400/10 text-yellow-400' },
  reject: { label: 'Reject', className: 'bg-red-400/10 text-red-400'      },
};

function RecentCallRow({ tc, navigate }) {
  const leadId = tc.shadowOriginalLeadId;
  const pill   = tc.decision ? DECISION_PILL[tc.decision] : null;
  const hasComplianceFlags = tc.judgeBreakdown?.complianceFlags?.length > 0;
  return (
    <div
      className={`flex items-start gap-3 py-2 px-3 rounded-lg hover:bg-civix-panelLight transition ${leadId ? 'cursor-pointer' : ''}`}
      onClick={() => leadId && navigate(`/leads?id=${leadId}`)}
    >
      <div className={`w-2 h-2 rounded-full shrink-0 mt-1.5 ${tc.status === 'ready' ? 'bg-civix-cyan' : tc.status === 'failed' ? 'bg-red-400' : 'bg-yellow-400'}`} />
      <div className="flex-1 min-w-0">
        <div className="text-sm text-white/80 truncate">
          {tc.callOutcome || 'any'} &middot; {fmtDuration(tc.durationSeconds)}
        </div>
        <div className="text-[10px] text-white/40">{fmt(tc.createdAt)}</div>
        {/* Phase 4: compliance flags */}
        {hasComplianceFlags && (
          <div className="flex items-center gap-1 mt-0.5">
            <ShieldAlert className="w-3 h-3 text-red-400 shrink-0" />
            <span className="text-[10px] text-red-400 truncate">
              {tc.judgeBreakdown.complianceFlags[0]}
            </span>
          </div>
        )}
      </div>
      {/* Phase 4: judge decision pill */}
      {pill && (
        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium shrink-0 ${pill.className}`}>
          {pill.label}
        </span>
      )}
      <span className={`text-xs px-2 py-0.5 rounded-full font-medium shrink-0 ${
        tc.status === 'ready'  ? 'bg-civix-cyan/10 text-civix-cyan' :
        tc.status === 'failed' ? 'bg-red-400/10 text-red-400' :
        'bg-yellow-400/10 text-yellow-400'
      }`}>
        {tc.status}
      </span>
      {leadId && <ChevronRight className="w-4 h-4 text-white/30 shrink-0" />}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function ShadowMode() {
  const navigate     = useNavigate();
  const queryClient  = useQueryClient();

  // ── Remote state ────────────────────────────────────────────────────────────
  const { data: configData, isLoading: configLoading } = useQuery({
    queryKey:  ['shadow-mode-config'],
    queryFn:   () => apiGet('/shadow-mode/config'),
    staleTime: 30000,
  });

  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey:  ['shadow-mode-stats'],
    queryFn:   () => apiGet('/shadow-mode/stats'),
    staleTime: 10000,
    refetchInterval: 30000,
  });

  const { data: botsData } = useQuery({
    queryKey:  ['bots'],
    queryFn:   () => apiGet('/bots'),
    staleTime: 60000,
  });

  // ── Local form state (initialised from server config) ────────────────────
  const [targetBotId,              setTargetBotId]              = useState('');
  const [enabled,                  setEnabled]                  = useState(false);
  const [minDurationSec,           setMinDurationSec]           = useState(60);
  const [requireOutcome,           setRequireOutcome]           = useState([]);
  const [autoRederivePersonaEvery, setAutoRederivePersonaEvery] = useState(10);
  const [strictMode,               setStrictMode]               = useState(true);  // Phase 4: default ON
  const [formDirty,                setFormDirty]                = useState(false);

  // Backfill state
  const [showBackfill,    setShowBackfill]    = useState(false);
  const [backfillCalls,   setBackfillCalls]   = useState([]);
  const [selectedCallIds, setSelectedCallIds] = useState(new Set());
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [ingestingIds,    setIngestingIds]    = useState(new Set());

  // Sync form from server config on load
  useEffect(() => {
    const cfg = configData?.config;
    if (cfg) {
      setTargetBotId(              String(cfg.targetBotId  || ''));
      setEnabled(                  cfg.enabled             ?? false);
      setMinDurationSec(           cfg.minDurationSec      ?? 60);
      setRequireOutcome(           cfg.requireOutcome      || []);
      setAutoRederivePersonaEvery( cfg.autoRederivePersonaEvery ?? 10);
      setStrictMode(               cfg.strictMode          ?? true);  // Phase 4
    }
    setFormDirty(false);
  }, [configData]);

  // ── Mutations ───────────────────────────────────────────────────────────────
  const saveMutation = useMutation({
    mutationFn: () => apiPut('/shadow-mode/config', {
      targetBotId,
      enabled,
      minDurationSec,
      requireOutcome,
      autoRederivePersonaEvery,
      strictMode,  // Phase 4
    }),
    onSuccess: () => {
      toast.success('Shadow Mode config saved');
      setFormDirty(false);
      queryClient.invalidateQueries({ queryKey: ['shadow-mode-config'] });
      queryClient.invalidateQueries({ queryKey: ['shadow-mode-stats'] });
    },
    onError: (err) => {
      toast.error(err?.response?.data?.error || 'Failed to save config');
    },
  });

  // ── Helpers ──────────────────────────────────────────────────────────────
  // Handle multiple response shapes: {bots:[]}, {data:[]}, [], or undefined
  const _botsArr = Array.isArray(botsData) ? botsData
                 : Array.isArray(botsData?.bots) ? botsData.bots
                 : Array.isArray(botsData?.data) ? botsData.data
                 : [];
  const ownedBots = _botsArr.filter((b) => b && b._isOwner !== false);

  function toggleOutcome(value) {
    setRequireOutcome((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
    setFormDirty(true);
  }

  function toggleCallSelection(id) {
    setSelectedCallIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  // ── Backfill ─────────────────────────────────────────────────────────────
  async function loadBackfillCalls() {
    setBackfillLoading(true);
    try {
      const params = requireOutcome.length ? { disposition: requireOutcome.join(',') } : {};
      const data = await apiGet('/shadow-mode/past-calls', params);
      setBackfillCalls(data.calls || []);
    } catch (err) {
      toast.error('Failed to load past calls');
    } finally {
      setBackfillLoading(false);
    }
  }

  async function ingestSelected() {
    if (!selectedCallIds.size) return;
    const ids = [...selectedCallIds];
    setIngestingIds(new Set(ids));
    let ok = 0, failed = 0;
    for (const callId of ids) {
      try {
        await apiPost('/shadow-mode/test-ingest', { callId });
        ok++;
      } catch {
        failed++;
      }
      setIngestingIds((prev) => { const n = new Set(prev); n.delete(callId); return n; });
    }
    setSelectedCallIds(new Set());
    queryClient.invalidateQueries({ queryKey: ['shadow-mode-stats'] });
    toast.success(`Ingested ${ok} call${ok !== 1 ? 's' : ''}${failed ? ` (${failed} failed)` : ''}`);
  }

  // ── Stats ─────────────────────────────────────────────────────────────────
  const stats            = statsData || {};
  const recentCalls      = stats.recentCalls || [];
  const reviewQueueCount = stats.reviewQueueCount ?? 0;

  if (configLoading) {
    return (
      <div className="flex flex-col h-full">
        <TopBar title="Shadow Mode" subtitle="Auto-learn from every manual call" />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-civix-purple animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <TopBar
        title="Shadow Mode"
        subtitle="Continuously train your bot from live manual calls"
        right={
          <button
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending || !targetBotId}
            className="btn-primary flex items-center gap-2"
          >
            {saveMutation.isPending
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <Save className="w-4 h-4" />
            }
            Save
          </button>
        }
      />

      {/* ── Enable toggle ──────────────────────────────────────────────────── */}
      <div className="panel p-5 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-civix-gradient flex items-center justify-center">
            <Mic2 className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="font-semibold text-white">Shadow Mode</div>
            <div className="text-sm text-white/50">
              When enabled, completed manual calls are automatically ingested into your target bot's training corpus.
            </div>
          </div>
        </div>
        <button
          onClick={() => { setEnabled((e) => !e); setFormDirty(true); }}
          className={`relative inline-flex h-7 w-14 shrink-0 rounded-full border-2 transition-colors duration-200 focus:outline-hidden ${
            enabled ? 'bg-civix-cyan border-civix-cyan' : 'bg-white/10 border-white/20'
          }`}
        >
          <span
            className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform duration-200 ${
              enabled ? 'translate-x-7' : 'translate-x-0.5'
            }`}
          />
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* ── Left: Config ─────────────────────────────────────────────────── */}
        <div className="lg:col-span-2 flex flex-col gap-4">

          {/* Bot selector */}
          <div className="panel p-5">
            <div className="flex items-center gap-2 mb-4">
              <Bot className="w-4 h-4 text-civix-purple" />
              <h3 className="font-semibold text-white">Target Bot</h3>
            </div>
            {ownedBots.length === 0 ? (
              <div className="text-sm text-white/50 bg-white/5 rounded-lg p-4">
                No bots found. <button className="text-civix-cyan underline" onClick={() => navigate('/bots')}>Create one first →</button>
              </div>
            ) : (
              <select
                value={targetBotId}
                onChange={(e) => { setTargetBotId(e.target.value); setFormDirty(true); }}
                className="input w-full"
              >
                <option value="">— Select a bot —</option>
                {ownedBots.map((b) => (
                  <option key={b._id} value={b._id}>{b.name}</option>
                ))}
              </select>
            )}
            <p className="text-xs text-white/40 mt-2 flex items-start gap-1">
              <Info className="w-3 h-3 mt-0.5 shrink-0" />
              All completed calls that pass your filters below will be transcribed by Whisper and ingested into this bot's RAG corpus.
            </p>
          </div>

          {/* Min duration slider */}
          <div className="panel p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-civix-purple" />
                <h3 className="font-semibold text-white">Minimum Duration</h3>
              </div>
              <span className="text-sm font-mono text-civix-cyan">
                {minDurationSec >= 60
                  ? `${Math.floor(minDurationSec / 60)}m ${minDurationSec % 60}s`
                  : `${minDurationSec}s`}
              </span>
            </div>
            <input
              type="range"
              min={30}
              max={600}
              step={30}
              value={minDurationSec}
              onChange={(e) => { setMinDurationSec(Number(e.target.value)); setFormDirty(true); }}
              className="w-full accent-civix-cyan"
            />
            <div className="flex justify-between text-[10px] text-white/30 mt-1">
              <span>30s</span><span>5m</span><span>10m</span>
            </div>
            <p className="text-xs text-white/40 mt-2">Calls shorter than this are skipped (avoids ingesting voicemail drops or hang-ups).</p>
          </div>

          {/* Outcome filter */}
          <div className="panel p-5">
            <div className="flex items-center gap-2 mb-4">
              <Filter className="w-4 h-4 text-civix-purple" />
              <h3 className="font-semibold text-white">Outcome Filter</h3>
              <span className="text-xs text-white/40 ml-auto">{requireOutcome.length === 0 ? 'All outcomes' : `${requireOutcome.length} selected`}</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {ALL_OUTCOMES.map((opt) => {
                const checked = requireOutcome.includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    onClick={() => toggleOutcome(opt.value)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-all ${
                      checked
                        ? 'bg-civix-purple/20 border border-civix-purple/50 text-white'
                        : 'bg-white/5 border border-white/10 text-white/60 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    {checked ? <CheckSquare className="w-4 h-4 text-civix-purple shrink-0" /> : <Square className="w-4 h-4 shrink-0" />}
                    {opt.label}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-white/40 mt-3">Leave all unchecked to ingest calls regardless of outcome.</p>
          </div>

          {/* Auto-rederive persona */}
          <div className="panel p-5">
            <div className="flex items-center gap-2 mb-3">
              <Zap className="w-4 h-4 text-civix-purple" />
              <h3 className="font-semibold text-white">Auto-Rederive Persona</h3>
            </div>
            <div className="flex items-center gap-4">
              <input
                type="number"
                min={1}
                max={50}
                value={autoRederivePersonaEvery}
                onChange={(e) => { setAutoRederivePersonaEvery(Math.max(1, Math.min(50, Number(e.target.value)))); setFormDirty(true); }}
                className="input w-24 text-center"
              />
              <span className="text-sm text-white/60">calls between each persona re-derivation</span>
            </div>
            <p className="text-xs text-white/40 mt-2">After every N shadow ingests, the bot's AgentDNA is rebuilt to incorporate the new training data. Lower = fresher persona, higher cost.</p>
          </div>

          {/* Phase 4: Strict Mode toggle */}
          <div className="panel p-5">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-civix-purple/20 flex items-center justify-center shrink-0">
                  {strictMode ? (
                    <ShieldCheck className="w-4 h-4 text-civix-purple" />
                  ) : (
                    <ShieldAlert className="w-4 h-4 text-yellow-400" />
                  )}
                </div>
                <div>
                  <div className="font-semibold text-white flex items-center gap-2">
                    Strict Mode
                    {strictMode ? (
                      <span className="text-xs px-1.5 py-0.5 rounded-full bg-civix-purple/20 text-civix-purple font-medium">ON</span>
                    ) : (
                      <span className="text-xs px-1.5 py-0.5 rounded-full bg-yellow-400/10 text-yellow-400 font-medium">OFF</span>
                    )}
                  </div>
                  <div className="text-xs text-white/50 mt-0.5">
                    {strictMode
                      ? 'Calls scored 40–70 by AI judge are held for manual review — never auto-ingested'
                      : 'Review-flagged calls are ingested immediately (outcome-only mode)'}
                  </div>
                </div>
              </div>
              <button
                onClick={() => { setStrictMode((s) => !s); setFormDirty(true); }}
                className={`relative inline-flex h-7 w-14 shrink-0 rounded-full border-2 transition-colors duration-200 focus:outline-hidden ${
                  strictMode ? 'bg-civix-purple border-civix-purple' : 'bg-white/10 border-white/20'
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform duration-200 ${
                    strictMode ? 'translate-x-7' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </div>
            <p className="text-xs text-white/30 mt-3 pl-12">
              Compliance flags always reject calls regardless of this setting.
            </p>
          </div>

          {/* Save button (also in topbar) */}
          {formDirty && (
            <button
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending || !targetBotId}
              className="btn-primary flex items-center justify-center gap-2 py-3"
            >
              {saveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save Changes
            </button>
          )}
        </div>

        {/* ── Right: Stats ──────────────────────────────────────────────────── */}
        <div className="flex flex-col gap-4">
          {/* Stat cards */}
          <StatCard icon={ArrowDownToLine} label="Total Ingests"   value={stats.ingestCount || 0} />
          <StatCard icon={Clock}           label="Last Ingest"     value={stats.lastIngestedAt  ? new Date(stats.lastIngestedAt).toLocaleDateString()  : '—'} sub={stats.lastIngestedAt  ? new Date(stats.lastIngestedAt).toLocaleTimeString()  : null} />
          <StatCard icon={RefreshCw}       label="Last Persona Re-derive" value={stats.lastRederivedAt ? new Date(stats.lastRederivedAt).toLocaleDateString() : '—'} sub={stats.lastRederivedAt ? new Date(stats.lastRederivedAt).toLocaleTimeString() : null} />

          {/* Phase 4: Review queue link */}
          {reviewQueueCount > 0 && targetBotId && (
            <button
              onClick={() => navigate(`/bots/${targetBotId}?tab=shadow-review`)}
              className="panel p-4 flex items-center gap-3 border border-yellow-400/30 bg-yellow-400/5 hover:bg-yellow-400/10 transition-colors text-left w-full"
            >
              <div className="w-9 h-9 rounded-lg bg-yellow-400/20 flex items-center justify-center shrink-0">
                <Eye className="w-4 h-4 text-yellow-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-yellow-400">{reviewQueueCount} call{reviewQueueCount !== 1 ? 's' : ''} awaiting review</div>
                <div className="text-xs text-white/40">Open Shadow Review tab →</div>
              </div>
              <ChevronRight className="w-4 h-4 text-white/30 shrink-0" />
            </button>
          )}

          {/* Recent calls */}
          <div className="panel p-4 flex-1">
            <div className="flex items-center gap-2 mb-3">
              <BarChart3 className="w-4 h-4 text-civix-purple" />
              <h3 className="font-semibold text-white text-sm">Recent Ingested Calls</h3>
            </div>
            {statsLoading ? (
              <div className="flex justify-center py-6">
                <Loader2 className="w-5 h-5 animate-spin text-white/40" />
              </div>
            ) : recentCalls.length === 0 ? (
              <div className="text-xs text-white/40 py-4 text-center">No calls ingested yet</div>
            ) : (
              <div className="space-y-1">
                {recentCalls.map((tc) => (
                  <RecentCallRow key={tc._id} tc={tc} navigate={navigate} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Backfill section ────────────────────────────────────────────────── */}
      <div className="panel p-5">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <RefreshCw className="w-4 h-4 text-civix-purple" />
            <h3 className="font-semibold text-white">Backfill from Past 30 Days</h3>
          </div>
          <button
            onClick={() => {
              if (!showBackfill) loadBackfillCalls();
              setShowBackfill((s) => !s);
            }}
            className="btn-secondary text-sm flex items-center gap-2"
          >
            {showBackfill ? 'Hide' : 'Show Calls'}
          </button>
        </div>
        <p className="text-xs text-white/40 mb-4">
          Select past completed calls that match your outcome filter and ingest them into the bot's training corpus.
        </p>

        {showBackfill && (
          <>
            {backfillLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-white/40" />
              </div>
            ) : backfillCalls.length === 0 ? (
              <div className="text-sm text-white/40 text-center py-6">No completed calls found in the past 30 days matching your filters.</div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-white/60">{backfillCalls.length} calls found</span>
                  <div className="flex gap-2">
                    <button
                      className="text-xs text-civix-cyan hover:underline"
                      onClick={() => setSelectedCallIds(new Set(backfillCalls.map((c) => c._id)))}
                    >
                      Select all
                    </button>
                    <span className="text-white/30">·</span>
                    <button
                      className="text-xs text-white/40 hover:text-white/60"
                      onClick={() => setSelectedCallIds(new Set())}
                    >
                      Clear
                    </button>
                  </div>
                </div>

                <div className="space-y-1 max-h-80 overflow-y-auto mb-4 pr-1">
                  {backfillCalls.map((call) => {
                    const isSelected = selectedCallIds.has(call._id);
                    const isIngesting = ingestingIds.has(call._id);
                    const leadName = call.lead
                      ? `${call.lead.firstName || ''} ${call.lead.lastName || ''}`.trim() || call.lead.phone
                      : 'Unknown lead';
                    return (
                      <div
                        key={call._id}
                        onClick={() => toggleCallSelection(call._id)}
                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all border ${
                          isSelected
                            ? 'bg-civix-purple/10 border-civix-purple/40'
                            : 'bg-white/5 border-transparent hover:border-white/10'
                        }`}
                      >
                        {isIngesting
                          ? <Loader2 className="w-4 h-4 animate-spin text-civix-cyan shrink-0" />
                          : isSelected
                            ? <CheckSquare className="w-4 h-4 text-civix-purple shrink-0" />
                            : <Square className="w-4 h-4 text-white/30 shrink-0" />
                        }
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-white/80 truncate">{leadName}</div>
                          <div className="text-xs text-white/40">
                            {fmtDuration(call.duration)} &middot; {call.disposition || 'no disposition'} &middot; {fmt(call.createdAt)}
                          </div>
                        </div>
                        {call.recordingUrl && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-civix-cyan/10 text-civix-cyan shrink-0">REC</span>
                        )}
                      </div>
                    );
                  })}
                </div>

                <button
                  onClick={ingestSelected}
                  disabled={selectedCallIds.size === 0 || ingestingIds.size > 0}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                >
                  {ingestingIds.size > 0
                    ? <><Loader2 className="w-4 h-4 animate-spin" />Ingesting...</>
                    : <><Play className="w-4 h-4" />Ingest {selectedCallIds.size} Selected Call{selectedCallIds.size !== 1 ? 's' : ''}</>
                  }
                </button>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
